import type { ComponentType } from 'react';

type ComponentEnhancer = (Component: ComponentType<any>) => ComponentType<any>;

/**
 * Composes higher-order components into a single enhancer, avoiding deeply
 * nested `a(b(c(Component)))` call pyramids.
 *
 * The order matches the reading order of nested calls: the first HOC in the
 * list becomes the outermost wrapper. The wrapped component keeps its original
 * type, so consumers see the same props signature as the input component.
 *
 * @example
 * // Equivalent to withA(withB(withC(Component)))
 * export default composeHOCs(withA, withB, withC)(Component);
 */
export function composeHOCs(
  ...hocs: ComponentEnhancer[]
): <C extends ComponentType<any>>(Component: C) => C {
  return <C extends ComponentType<any>>(Component: C): C =>
    hocs.reduceRight<ComponentType<any>>(
      (wrapped, hoc) => hoc(wrapped),
      Component
    ) as C;
}
